import {
  HiOutlineArrowsRightLeft,
  HiOutlineFolderPlus,
  HiOutlinePencilSquare,
  HiOutlineUserGroup,
} from "react-icons/hi2";

import {
  folderChain,
  type FileFolder,
  type FileFolderScope,
} from "../entities/files";

/**
 * Шапка выбранной папки: путь, название и кнопки правки.
 *
 * Кнопки показываются по `can_manage` самой папки, а не по общему праву:
 * папка соседнего КПО может быть открыта на чтение человеку, у которого право
 * на свои папки есть. Кнопки удаления папки здесь нет и быть не может — в
 * контракте такой операции нет.
 */
export function FolderSummary({
  folder,
  byId,
  granted,
  grantedError,
  readOnlyReason,
  onOpenFolder,
  onRename,
  onMove,
  onAccess,
  onCreateInside,
}: {
  folder: FileFolder;
  byId: ReadonlyMap<string, FileFolder>;
  /** КПО, которым папка открыта; `null`, пока не получено или недоступно. */
  granted: readonly FileFolderScope[] | null;
  grantedError: string | null;
  /** Почему папку нельзя менять; берётся для папок без `can_manage`. */
  readOnlyReason: string;
  onOpenFolder: (id: string | null) => void;
  onRename: () => void;
  onMove: () => void;
  onAccess: () => void;
  /** Создать папку внутри этой: есть и у чужой открытой папки, там заводят свои папки. */
  onCreateInside: (() => void) | null;
}) {
  const chain = folderChain(byId, folder.id);

  return (
    <section className="folder-summary" aria-labelledby="folder-summary-title">
      <nav className="folder-summary__path" aria-label="Путь к папке">
        <ol>
          <li>
            <button
              className="button button--text button--compact"
              type="button"
              onClick={() => onOpenFolder(null)}
            >
              Все файлы
            </button>
          </li>
          {chain.map((link, index) => {
            const last = index === chain.length - 1;

            return (
              <li
                key={`${link.id ?? "stub"}:${index}`}
                aria-current={last ? "location" : undefined}
              >
                {last || link.id === null ? (
                  <span>{link.name}</span>
                ) : (
                  <button
                    className="button button--text button--compact"
                    type="button"
                    onClick={() => onOpenFolder(link.id)}
                  >
                    {link.name}
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
      <div className="folder-summary__head">
        <h2 id="folder-summary-title">{folder.name}</h2>
        {folder.can_manage || onCreateInside ? (
          <div className="inline-actions">
            {onCreateInside ? (
              <button
                className="button button--secondary button--compact"
                type="button"
                onClick={onCreateInside}
              >
                <HiOutlineFolderPlus aria-hidden="true" />
                Новая подпапка
              </button>
            ) : null}
            {folder.can_manage ? (
              <>
                <button
                  className="button button--secondary button--compact"
                  type="button"
                  onClick={onRename}
                >
                  <HiOutlinePencilSquare aria-hidden="true" />
                  Переименовать
                </button>
                <button
                  className="button button--secondary button--compact"
                  type="button"
                  onClick={onMove}
                >
                  <HiOutlineArrowsRightLeft aria-hidden="true" />
                  Перенести
                </button>
                <button
                  className="button button--secondary button--compact"
                  type="button"
                  onClick={onAccess}
                >
                  <HiOutlineUserGroup aria-hidden="true" />
                  Доступ КПО
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
      {folder.can_manage ? (
        <p className="muted folder-summary__access">
          {granted === null
            ? grantedError
              ? `Не удалось узнать, каким КПО открыта папка: ${grantedError}.`
              : "Загружаем доступ…"
            : granted.length === 0
              ? "Видна только организации-владельцу."
              : `Дополнительно открыта КПО: ${granted.map((scope) => scope.name).join(", ")}.`}
        </p>
      ) : (
        <p className="muted folder-summary__access">{readOnlyReason}</p>
      )}
    </section>
  );
}
